// What an hour costs and what it makes (CFO plan phase 1). Pure.
//
// Every figure comes in two versions:
//   actual       — what the books see: only real pay counts, so an unpaid owner's hours cost $0.
//   if paid out  — everyone's hours costed, at their shadow rate where one is set (Lj: $41.50/h).
//                  Never posted anywhere; screens label it "not booked".
//
// Pay per person comes from the books (`pay` = opsPay/{uid}: payType, costRatePerHour, shadowRatePerHour).
// The job's money comes from the linked books job (`jobs` = opsJobs/{jobId}) when there is one.

import { resolveRate } from './hierarchy.js';
import { payWeekStart } from './timesheet.js';
import { localDateStr } from './time.js';
import { canonTag, resolveTags } from './tags.js';

const H = 3600000;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (v) => Math.round(v * 100) / 100;

export const PAY_TYPES = ['hourly', 'salary', 'piece', 'share', 'sub', 'unpaid'];
export const PAY_TYPE_LABELS = { hourly: 'Hourly', salary: 'Salary', piece: 'Piece rate', share: 'Task share', sub: 'Subcontractor', unpaid: 'Unpaid' };

/** How the work makes money, or that it doesn't (CFO note §7). A label only — no posting hangs off it. */
export const JOB_TYPES = ['fixed', 'hourly', 'product', 'internal', 'personal'];
export const JOB_TYPE_LABELS = { fixed: 'Fixed price', hourly: 'Hourly (T&M)', product: 'Product', internal: 'Internal', personal: 'Personal' };
/** Types whose hours are meant to earn: these want a books job, an estimate, a contract value. */
export const BILLABLE_JOB_TYPES = new Set(['fixed', 'hourly', 'product']);

/**
 * One person's cost per hour, both versions. `pay` = their opsPay doc (null = not set).
 * → { actual, paidOut, payType, shadow, missing: null | 'pay type' | 'cost rate' }
 */
export function personRates(pay, jobPay = null, policy = null) {
  if (jobPay && PAY_TYPES.includes(jobPay.payType)) return jobRates(pay, jobPay, policy);
  const payType = PAY_TYPES.includes(pay?.payType) ? pay.payType : null;
  // Phase 2: the books publish loaded rates (employer costs on top); fall back to the plain ones before that.
  const cost = pay?.loadedCostPerHour != null ? num(pay.loadedCostPerHour) : pay?.costRatePerHour == null ? null : num(pay.costRatePerHour);
  const shadowRaw = pay?.shadowRatePerHour == null ? null : num(pay.shadowRatePerHour);
  const shadow = shadowRaw == null ? null : pay?.loadedShadowPerHour != null ? num(pay.loadedShadowPerHour) : shadowRaw;
  let actual = 0; let missing = null;
  if (!payType) { actual = cost || 0; missing = 'pay type'; }
  else if (payType === 'unpaid') actual = 0;
  else { actual = cost || 0; if (cost == null) missing = 'cost rate'; }
  const burden = pay?.burden || null;
  return { actual, paidOut: shadow != null ? shadow : actual, payType, shadow, shadowRaw, missing, loaded: !!(pay?.loadedCostPerHour != null || pay?.loadedShadowPerHour != null), wcbCounted: burden ? burden.wcbCounted !== false : null, burdenPct: burden?.pct ?? null };
}

/** Books jobs for this project's customer, the ones to offer first when linking: the job's customer named in the project's customer or name. */
export function suggestBooksJobs(project, jobs = []) {
  const who = String(project?.customerName || project?.name || '').toLowerCase();
  return (jobs || []).filter((j) => { const c = String(j?.customerName || '').trim().toLowerCase(); return c.length >= 4 && who.includes(c) && j.status !== 'cancelled'; });
}

/** The job type: the project's own wins; else read off the linked books job; else Personal by department. */
export function jobTypeOf(project, job = null, dept = null) {
  if (JOB_TYPES.includes(project?.jobType)) return project.jobType;
  const ct = job?.costingType;
  if (ct === 'fixed_price') return 'fixed';
  if (ct === 'time_and_materials' || ct === 'cost_plus' || ct === 'not_to_exceed') return 'hourly';
  if (dept && (dept.id === 'personal' || /^personal$/i.test(String(dept.name || '')))) return 'personal';
  return null;
}

/**
 * % complete by estimate: estimated minutes of finished tasks ÷ all estimated minutes. A parent whose
 * subtasks carry their own estimates (an imported bid assembly) counts through them, not on top.
 * `doneWhen(t)` narrows "finished" (the day report: finished today). → { totalMin, doneMin, pct }
 */
export function estimateProgress(tasks = [], doneWhen = (t) => t.status === 'done') {
  const parents = new Set(tasks.filter((t) => t.parentId && num(t.estimatedMinutes) > 0).map((t) => t.parentId));
  let totalMin = 0; let doneMin = 0;
  for (const t of tasks) {
    if (parents.has(t.id) || !(num(t.estimatedMinutes) > 0)) continue;
    totalMin += num(t.estimatedMinutes);
    if (doneWhen(t)) doneMin += num(t.estimatedMinutes);
  }
  return { totalMin, doneMin, pct: totalMin > 0 ? doneMin / totalMin : null };
}

/** The contract the job is worth: the books job's (with change orders) when linked, else the project's value. */
export function contractValueOf(project, job = null) {
  return num(job?.contractValue) > 0 ? num(job.contractValue) : num(project?.value);
}

/**
 * What the work has earned so far.
 *   fixed  → contract value × % complete by estimate
 *   hourly → hours × bill rate (project, else department)
 *   others → nothing earned by the hour (a product earns when sold)
 * Money already collected on the books job (or logged as income) replaces the estimate once it's higher.
 * → { amount, basis: 'earned' | 'booked' | null, earned, booked, pct, contract }
 */
export function earnedRevenue({ type, project, job = null, dept = null, tasks = [], hours = 0, booked = 0 }) {
  let earned = 0; let pct = null; let contract = null;
  if (type === 'fixed') {
    contract = contractValueOf(project, job);
    pct = estimateProgress(tasks).pct;
    if (contract > 0 && pct != null) earned = contract * pct;
  } else if (type === 'hourly') {
    earned = hours * num(resolveRate(project, dept));
  }
  const paid = Math.max(num(job?.paid), num(booked));
  const amount = Math.max(earned, paid);
  return {
    amount: round2(amount), basis: amount <= 0 ? null : paid >= earned ? 'booked' : 'earned',
    earned: round2(earned), booked: round2(paid), pct, contract,
  };
}

/** Job costs on the books job, apart from labour (labour is costed from the hours, per person). */
export function jobCostsOf(job) {
  if (!job?.actual) return null;
  return round2(Object.entries(job.actual).reduce((m, [k, v]) => (k === 'labor' ? m : m + num(v)), 0));
}

/**
 * Labour, per person, both versions. `msByUid` = Map(uid → ms worked); `pay` = { [uid]: opsPay doc }.
 * → { actual, paidOut, byPerson: [{ uid, ms, hours, rates, actual, paidOut }] }
 */
export function labourCost(msByUid, pay = {}, { jobPays = {}, overrides = {}, policy = null } = {}) {
  const byPerson = [...msByUid.entries()].filter(([uid, ms]) => ms > 0 || overrides[uid]).map(([uid, ms]) => {
    const rates = personRates(pay?.[uid] || null, jobPays[uid] || null, policy);
    const hours = ms / H;
    // Phase 3: piece pay (Σ finished pieces) and salary (per pay week) are worked out by the caller, per person.
    const o = overrides[uid];
    if (o) return { uid, ms, hours: round2(hours), rates, actual: round2(o.actual), paidOut: round2(o.paidOut), basis: o.basis, effectiveHourly: hours > 0 ? round2(o.actual / hours) : null };
    return { uid, ms, hours: round2(hours), rates, actual: round2(hours * rates.actual), paidOut: round2(hours * rates.paidOut) };
  }).sort((a, b) => b.ms - a.ms);
  return {
    actual: round2(byPerson.reduce((m, p) => m + p.actual, 0)),
    paidOut: round2(byPerson.reduce((m, p) => m + p.paidOut, 0)),
    byPerson,
  };
}

/** Profit and profit per hour, both versions, from the parts. `overhead` = { actual, paidOut } charged to this work. */
export function profitOf({ revenue = 0, jobCosts = 0, labour, hours = 0, overhead = null }) {
  const oa = num(overhead?.actual); const op = num(overhead?.paidOut);
  const actual = round2(revenue - jobCosts - labour.actual - oa);
  const paidOut = round2(revenue - jobCosts - labour.paidOut - op);
  const per = (v) => (hours > 0 ? round2(v / hours) : null);
  return {
    profit: { actual, paidOut },
    perHour: { revenue: per(revenue), profitActual: per(actual), profitPaidOut: per(paidOut), costPaidOut: per(jobCosts + labour.paidOut + op) },
    marginPct: revenue > 0 ? { actual: Math.round((actual / revenue) * 100), paidOut: Math.round((paidOut / revenue) * 100) } : null,
  };
}

/** "$41.50" / "−$12" / "$1,240" — whole dollars past $100. */
export function moneyShort(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  const s = a >= 100 ? Math.round(a).toLocaleString('en-CA') : a.toFixed(2).replace(/\.00$/, '');
  return `${v < 0 ? '−' : ''}$${s}`;
}

/**
 * Assistant accounts (CFO 5.4): opsMembers publish `agent: true` for them. Their time never counts as hours — every
 * money figure skips sessions they own. → Set of uids.
 */
export function agentUids(members = []) {
  return new Set((members || []).filter((m) => m?.agent).map((m) => m.uid || m.id).filter(Boolean));
}
const isAgent = (agents, uid) => !!(agents && uid && agents.has(uid));

const sumBoth = (list, k) => ({ actual: round2(list.reduce((a, x) => a + num(x[k]?.actual), 0)), paidOut: round2(list.reduce((a, x) => a + num(x[k]?.paidOut), 0)) });

/**
 * The day's money (the Daily Log's Money section): per job worked, its hours, what the day's work
 * earned, labour per person both versions, and profit. Personal jobs are left out.
 *   work      the day's real work sessions, clipped to the day ({ projectId, ownerUid, ms })
 *   inDay(ms) is this timestamp on the day
 *   agents    assistant uids (agentUids) — their sessions are skipped
 *   taskTime  the day's task time ([{ taskId, task, ms }], dayreport) for the per-task rows
 *   tagIndex  the suite tag list (tags.js tagIndex) — merged tags read as their target in `byTag`
 * Earned today: fixed → contract × estimate of tasks finished today ÷ the job's whole estimate;
 * hourly → hours × bill rate. Job costs (receipts) aren't dated to a day's work — the project view has them.
 * CFO 5.7 adds: per task (its estimate share × contract on fixed, hours × rate on hourly, and its profit), per person
 * (earned, profit per billable hour, non-billable hours apart), per job type and per tag, the company's billable ÷
 * clocked, and the day's absorption (overhead charged to the day's billable hours against a day of the pool).
 */
export function dayMoney({ work = [], tasks = [], projects = [], jobs = [], departments = [], pay = {}, me = null, inDay = () => false, overhead = null, agents = null, taskTime = [], tagIndex = null }) {
  const projById = new Map(projects.map((p) => [p.id, p]));
  const jobById = new Map(jobs.map((j) => [j.id, j]));
  const deptById = new Map(departments.map((d) => [d.id, d]));
  const typeOfP = (project) => jobTypeOf(project, project.bookProjectId ? jobById.get(project.bookProjectId) || null : null, deptById.get(project.departmentId) || null);
  const per = new Map();
  const clockedBy = new Map();   // uid → { clocked, billable } ms, every work session (no project too)
  for (const s of work) {
    const uid = s.ownerUid || me || '_me';
    if (isAgent(agents, uid)) continue;
    const c = clockedBy.get(uid) || { clocked: 0, billable: 0 };
    c.clocked += s.ms || 0;
    if (s.projectId && BILLABLE_JOB_TYPES.has(typeOfP(projById.get(s.projectId) || { id: s.projectId }))) c.billable += s.ms || 0;
    clockedBy.set(uid, c);
    if (!s.projectId) continue;
    const m = per.get(s.projectId) || new Map();
    m.set(uid, (m.get(uid) || 0) + (s.ms || 0));
    per.set(s.projectId, m);
  }
  const rows = [];
  const fixedShare = new Map();   // projectId → { contract, totalMin, parents } for per-task earned
  for (const [pid, msByUid] of per) {
    const project = projById.get(pid) || { id: pid };
    const job = project.bookProjectId ? jobById.get(project.bookProjectId) || null : null;
    const type = typeOfP(project);
    if (type === 'personal') continue;
    const ms = [...msByUid.values()].reduce((a, v) => a + v, 0);
    const hours = ms / H;
    let earned = 0;
    if (type === 'fixed') {
      const mine = tasks.filter((t) => t.projectId === pid && !t.bucket);
      const contract = contractValueOf(project, job);
      const today = estimateProgress(mine, (t) => t.status === 'done' && inDay(t.completedAt));
      if (contract > 0 && today.totalMin > 0) earned = (contract * today.doneMin) / today.totalMin;
      fixedShare.set(pid, { contract, totalMin: today.totalMin, parents: new Set(mine.filter((t) => t.parentId && num(t.estimatedMinutes) > 0).map((t) => t.parentId)) });
    } else if (type === 'hourly') {
      earned = hours * num(resolveRate(project, deptById.get(project.departmentId) || null));
    }
    const labour = labourCost(msByUid, pay);
    const oh = overheadFor(type, hours, overhead);
    rows.push({ projectId: pid, name: project.name || 'Project', type, hours: round2(hours), earned: round2(earned), labour, overhead: oh, msByUid, ...profitOf({ revenue: earned, labour, hours, overhead: oh }) });
  }
  rows.sort((a, b) => b.hours - a.hours);
  const hours = rows.reduce((a, r) => a + r.hours, 0);
  const earned = round2(rows.reduce((a, r) => a + r.earned, 0));
  const byUid = new Map();
  for (const r of rows) for (const p of r.labour.byPerson) {
    const t = byUid.get(p.uid) || { uid: p.uid, hours: 0, actual: 0, paidOut: 0, rates: p.rates };
    t.hours = round2(t.hours + p.hours); t.actual = round2(t.actual + p.actual); t.paidOut = round2(t.paidOut + p.paidOut);
    byUid.set(p.uid, t);
  }
  const labour = { actual: round2(rows.reduce((a, r) => a + r.labour.actual, 0)), paidOut: round2(rows.reduce((a, r) => a + r.labour.paidOut, 0)), byPerson: [...byUid.values()].sort((a, b) => b.hours - a.hours) };
  const overheadTotal = { actual: round2(rows.reduce((a, r) => a + r.overhead.actual, 0)), paidOut: round2(rows.reduce((a, r) => a + r.overhead.paidOut, 0)) };

  // ── per task: its share of the day's earned, its hours costed by who did it ──
  const rowBy = new Map(rows.map((r) => [r.projectId, r]));
  const timeBy = new Map((taskTime || []).filter((x) => x?.taskId).map((x) => [x.taskId, num(x.ms)]));
  const tagsOf = (list) => [...new Set((tagIndex ? resolveTags(list, tagIndex) : list || []).map(canonTag).filter(Boolean))];
  const taskRows = [];
  for (const t of tasks) {
    if (!t?.id || t.bucket || !rowBy.has(t.projectId)) continue;
    const r = rowBy.get(t.projectId);
    const ms = timeBy.get(t.id) || 0;
    let e = 0;
    const fx = fixedShare.get(t.projectId);
    if (r.type === 'fixed' && fx && t.status === 'done' && inDay(t.completedAt) && !fx.parents.has(t.id) && fx.contract > 0 && fx.totalMin > 0) e = (fx.contract * num(t.estimatedMinutes)) / fx.totalMin;
    else if (r.type === 'hourly') e = (ms / H) * num(resolveRate(projById.get(t.projectId) || {}, deptById.get(projById.get(t.projectId)?.departmentId) || null));
    if (!(ms > 0) && !(e > 0)) continue;
    const uid = t.assignee || t.ownerUid || me || '_me';
    if (isAgent(agents, uid)) continue;
    const h = ms / H;
    const rr = personRates(pay?.[uid] || null);
    const lab = { actual: round2(h * rr.actual), paidOut: round2(h * rr.paidOut) };
    const oh = overheadFor(r.type, h, overhead);
    const project = projById.get(t.projectId) || {};
    taskRows.push({
      taskId: t.id, title: t.title || 'Task', projectId: t.projectId, type: r.type, uid, hours: round2(h), earned: round2(e),
      done: t.status === 'done' && inDay(t.completedAt), labour: lab, overhead: oh, tags: tagsOf([...(t.tags || []), ...(project.tags || [])]),
      viaTags: tagsOf(project.tags || []).filter((k) => !tagsOf(t.tags || []).includes(k)),   // the project's tags the task doesn't carry itself (projecttags.js)
      ...profitOf({ revenue: e, labour: lab, hours: h, overhead: oh }),
    });
  }
  taskRows.sort((a, b) => b.earned - a.earned || b.hours - a.hours);

  // ── per person: what their hours earned (their share of each job's day), profit per billable hour ──
  const people = [];
  for (const [uid, c] of clockedBy) {
    let e = 0; const lab = { actual: 0, paidOut: 0 }; const oh = { actual: 0, paidOut: 0 };
    for (const r of rows) {
      const ms = r.msByUid.get(uid) || 0;
      if (!ms) continue;
      const share = r.hours > 0 ? ms / H / r.hours : 0;
      e += r.earned * share;
      const lp = r.labour.byPerson.find((p) => p.uid === uid);
      lab.actual += num(lp?.actual); lab.paidOut += num(lp?.paidOut);
      oh.actual += r.overhead.actual * share; oh.paidOut += r.overhead.paidOut * share;
    }
    const clocked = c.clocked / H; const billable = c.billable / H;
    const profit = { actual: round2(e - lab.actual - oh.actual), paidOut: round2(e - lab.paidOut - oh.paidOut) };
    people.push({
      uid, hours: round2(clocked), billableHours: round2(billable), nonBillableHours: round2(clocked - billable), earned: round2(e),
      labour: { actual: round2(lab.actual), paidOut: round2(lab.paidOut) }, overhead: { actual: round2(oh.actual), paidOut: round2(oh.paidOut) }, profit,
      perHour: { earned: billable > 0 ? round2(e / billable) : null, profitActual: billable > 0 ? round2(profit.actual / billable) : null, profitPaidOut: billable > 0 ? round2(profit.paidOut / billable) : null },
    });
  }
  people.sort((a, b) => b.hours - a.hours);

  // ── per job type and per tag ──
  const group = (list, keysOf) => {
    const m = new Map();
    for (const x of list) for (const k of keysOf(x)) {
      const g = m.get(k) || { key: k, hours: 0, earned: 0, items: [] };
      g.hours += x.hours; g.earned += x.earned; g.items.push(x);
      m.set(k, g);
    }
    return [...m.values()].map((g) => {
      const lab = sumBoth(g.items, 'labour'); const oh = sumBoth(g.items, 'overhead');
      return { key: g.key, hours: round2(g.hours), earned: round2(g.earned), labour: lab, overhead: oh, ...profitOf({ revenue: g.earned, labour: lab, hours: g.hours, overhead: oh }) };
    }).sort((a, b) => b.earned - a.earned || b.hours - a.hours);
  };
  const byType = group(rows, (r) => [r.type || 'untyped']).map(({ key, ...x }) => ({ type: key, ...x }));
  // A task's own tags and its project's tags, each once (a task carrying its project's tag isn't counted twice);
  // viaProjectHours = the hours that came only through the project's tag.
  const viaH = new Map();
  for (const t of taskRows) for (const k of t.viaTags || []) viaH.set(k, (viaH.get(k) || 0) + t.hours);
  const byTag = group(taskRows, (t) => t.tags).map(({ key, ...x }) => ({ tag: key, ...x, viaProjectHours: round2(viaH.get(key) || 0) }));

  // ── the company: billable ÷ clocked; the day's absorption ──
  const clockedMs = [...clockedBy.values()].reduce((a, c) => a + c.clocked, 0);
  const billableMs = [...clockedBy.values()].reduce((a, c) => a + c.billable, 0);
  const utilization = { clocked: round2(clockedMs / H), billable: round2(billableMs / H), nonBillable: round2((clockedMs - billableMs) / H), pct: clockedMs > 0 ? Math.round((billableMs / clockedMs) * 100) : null };
  let dayAbsorption = null;
  if (overhead && overhead.pool != null && num(overhead.to) > num(overhead.from)) {
    const days = (num(overhead.to) - num(overhead.from)) / 86400000;
    const pool = { actual: round2(num(overhead.pool) / days), paidOut: round2(num(overhead.poolPaidOut ?? overhead.pool) / days) };
    dayAbsorption = { billableHours: utilization.billable, charged: overheadTotal, pool, over: { actual: round2(overheadTotal.actual - pool.actual), paidOut: round2(overheadTotal.paidOut - pool.paidOut) } };
  }

  return {
    rows: rows.map(({ msByUid, ...r }) => r), tasks: taskRows, people, byType, byTag, utilization, absorption: dayAbsorption,
    total: { hours: round2(hours), earned, labour, overhead: overheadTotal, ...profitOf({ revenue: earned, labour, hours, overhead: overheadTotal }) },
  };
}


// ---- overhead (phase 2) -------------------------------------------------------------------------------------------

const sessionMsIn = (s, from, to, now) => {
  const start = Math.max(num(s.startedAt), from);
  const end = Math.min(s.endedAt ? num(s.endedAt) : now, to);
  return end > start ? end - start : 0;
};

/**
 * Overhead per billable hour (CFO §7). The books publish the pool (opsRates/overhead: 90 days of operating expenses,
 * by role); billable hours = clocked hours on fixed / hourly / product jobs over the same window.
 *   actual   = pool ÷ billable hours
 *   paidOut  = (pool + internal hours costed at the gap to their if-paid-out rate, e.g. an unpaid owner's shadow pay)
 *              ÷ billable hours — the "full cost" view
 * Assistant accounts' sessions (`agents`, agentUids) are skipped: their time never counts as hours (CFO 5.4).
 * → { actual, paidOut, billableHours, internalHours, untypedHours, pool, poolPaidOut, from, to } | null (no pool / hours)
 */
export function overheadRate({ rates, sessions = [], projects = [], jobs = [], departments = [], pay = {}, me = null, now = Date.now(), agents = null }) {
  if (!rates || rates.pool == null) return null;
  const to = num(rates.window?.to) || now; const from = num(rates.window?.from) || to - 90 * 86400000;
  const projById = new Map(projects.map((p) => [p.id, p]));
  const jobById = new Map(jobs.map((j) => [j.id, j]));
  const deptById = new Map(departments.map((d) => [d.id, d]));
  let billable = 0; let untyped = 0; let internal = 0; let extra = 0;
  for (const s of sessions) {
    if (!s || s.overlay || s.type === 'travel' || !s.projectId || isAgent(agents, s.ownerUid)) continue;
    const ms = sessionMsIn(s, from, to, now);
    if (!ms) continue;
    const p = projById.get(s.projectId) || { id: s.projectId };
    const type = jobTypeOf(p, p.bookProjectId ? jobById.get(p.bookProjectId) || null : null, deptById.get(p.departmentId) || null);
    if (BILLABLE_JOB_TYPES.has(type)) billable += ms;
    else if (type === 'internal') {
      internal += ms;
      const r = personRates(pay[s.ownerUid || me] || null);
      extra += (ms / H) * Math.max(0, r.paidOut - r.actual);
    } else if (!type) untyped += ms;
  }
  const bh = billable / H;
  const pool = num(rates.pool); const poolPaidOut = round2(pool + extra);
  return {
    actual: bh > 0 ? round2(pool / bh) : null, paidOut: bh > 0 ? round2(poolPaidOut / bh) : null,
    billableHours: round2(bh), internalHours: round2(internal / H), untypedHours: round2(untyped / H),
    pool: round2(pool), poolPaidOut, from, to,
  };
}

/** Overhead charged for `hours` of work of this type: billable types carry it, the rest don't. */
export function overheadFor(type, hours, rate) {
  if (!rate || !BILLABLE_JOB_TYPES.has(type) || !(hours > 0)) return { actual: 0, paidOut: 0 };
  return { actual: round2(hours * num(rate.actual)), paidOut: round2(hours * num(rate.paidOut)) };
}

/**
 * Under/over-absorbed overhead for a month (books view): overhead charged to jobs (billable hours that month × the
 * actual rate) against the month's actual pool. Positive `over` = charged more than was spent.
 */
export function absorption({ month, rate, rates, sessions = [], projects = [], jobs = [], departments = [], now = Date.now(), agents = null }) {
  if (!rate || rate.actual == null || !rates) return null;
  const [y, m] = month.split('-').map(Number);
  const from = new Date(y, m - 1, 1).getTime(); const to = Math.min(new Date(y, m, 1).getTime(), now);
  const r = overheadRate({ rates: { pool: 0, window: { from, to } }, sessions, projects, jobs, departments, now, agents });
  const charged = round2((r?.billableHours || 0) * rate.actual);
  const actual = round2(num(rates.months?.[month]));
  return { month, billableHours: r?.billableHours || 0, charged, actual, over: round2(charged - actual) };
}


// ---- people on jobs (phase 3) ---------------------------------------------------------------------------------------

/** The per-job pay doc id (opsJobPay): one person's pay on one job. */
export const jobPayId = (projectId, uid) => `${projectId}__${uid}`;

/** { [uid]: opsJobPay doc } for one project. */
export function jobPaysFor(jobPays = [], projectId) {
  const out = {};
  for (const j of jobPays) if (j && j.projectId === projectId && j.uid) out[j.uid] = j;
  return out;
}

/**
 * Rates when a job sets its own pay for this person (the add-person wizard). An employee's wage gets the employer
 * burden on top (theirs from the books, else the business's); a sub's rate is what they bill; unpaid keeps the
 * shadow; piece is priced per task (rates here are 0 — the pieces carry the cost).
 */
function jobRates(pay, jobPay, policy) {
  const base = personRates(pay);
  const rate = num(jobPay.rate);
  const burdenPct = num(pay?.burden?.pct ?? policy?.burdenPct);
  let actual = 0;
  if (jobPay.payType === 'hourly' || jobPay.payType === 'salary') actual = rate > 0 ? round2(rate * (1 + burdenPct / 100)) : base.actual;
  else if (jobPay.payType === 'sub') actual = rate > 0 ? rate : base.actual;
  else if (jobPay.payType === 'unpaid') actual = 0;
  else if (jobPay.payType === 'share') actual = base.actual;   // P7: costed at their hourly floor; the share itself is shown, not costed twice
  const paidOut = jobPay.payType === 'unpaid' ? (base.shadow ?? 0) : jobPay.payType === 'piece' ? 0 : actual;
  return { ...base, payType: jobPay.payType, actual, paidOut, jobRate: rate || null, onJob: true, missing: jobPay.payType === 'piece' || jobPay.payType === 'share' || actual > 0 || jobPay.payType === 'unpaid' ? null : 'rate on this job' };
}

/** Piece pay owed on a job, per worker: finished tasks carrying `piece { price, uid }`. → { [uid]: { amount, count } } */
export function pieceCostByWorker(tasks = [], projectId) {
  const out = {};
  for (const t of tasks) {
    if (t.projectId !== projectId || t.status !== 'done' || !t.piece?.uid || !(num(t.piece.price) > 0)) continue;
    const o = out[t.piece.uid] || (out[t.piece.uid] = { amount: 0, count: 0 });
    o.amount = round2(o.amount + num(t.piece.price)); o.count += 1;
  }
  return out;
}

/**
 * A salaried person's cost per hour, week by week: salary per pay week ÷ the hours they clocked that week, loaded.
 * A slow week makes each hour cost more (CFO §15). → (ms) of a session → $ for that session, via `costOf(session)`.
 */
export function salaryCost({ uid, salary, sessions = [], weekStart = 0, burdenPct = 0, now = Date.now() }) {
  const weekly = (num(salary?.perPeriod) * (num(salary?.periodsPerYear) || 26)) / 52;
  const weekOf = (ms) => payWeekStart(localDateStr(ms), weekStart);
  const msOf = (s) => (s.endedAt ? (num(s.duration) || num(s.endedAt) - num(s.startedAt)) : Math.max(0, now - num(s.startedAt)));
  const hours = new Map();
  for (const s of sessions) {
    if (!s || s.ownerUid !== uid || s.overlay || s.type === 'travel') continue;
    const w = weekOf(num(s.startedAt));
    hours.set(w, (hours.get(w) || 0) + msOf(s) / H);
  }
  const rateFor = (w) => { const h = hours.get(w) || 0; return h > 0 ? (weekly / h) * (1 + burdenPct / 100) : 0; };
  return {
    weekly: round2(weekly),
    rateFor: (ms) => round2(rateFor(weekOf(ms))),
    costOf: (s) => (msOf(s) / H) * rateFor(weekOf(num(s.startedAt))),
  };
}

// ---- equipment (phase 3) ------------------------------------------------------------------------------------------

/**
 * Cost per km (vehicles) or per hour (tools) from the books' opsEquipment docs: (monthly not-in-books × 3 + 90 days of
 * tagged costs) ÷ 90 days of use — km driven (travel sessions) or clocked hours on jobs the tool is assigned to.
 * → { [id]: { name, unit, perUnit, usage, cost } }
 */
export function equipmentRates({ equipment = [], sessions = [], projects = [], now = Date.now() }) {
  const out = {};
  const from = now - 90 * 86400000;
  let km = 0;
  for (const s of sessions) if (s && s.type === 'travel' && num(s.startedAt) >= from) km += num(s.distanceM) / 1000;
  const toolHours = new Map();
  const onJob = new Map(projects.map((p) => [p.id, Array.isArray(p.equipment) ? p.equipment : []]));
  for (const s of sessions) {
    if (!s || s.overlay || s.type === 'travel' || !s.projectId || num(s.startedAt) < from) continue;
    const ms = s.endedAt ? (num(s.duration) || num(s.endedAt) - num(s.startedAt)) : Math.max(0, now - num(s.startedAt));
    for (const id of onJob.get(s.projectId) || []) toolHours.set(id, (toolHours.get(id) || 0) + ms / H);
  }
  for (const e of equipment) {
    if (!e?.id) continue;
    const cost = num(e.monthlyCost) * 3 + num(e.runningCost90);
    const usage = e.unit === 'hour' ? (toolHours.get(e.id) || 0) : km;
    out[e.id] = { name: e.name || 'Equipment', unit: e.unit === 'hour' ? 'hour' : 'km', perUnit: usage > 0 ? round2(cost / usage) : null, usage: round2(usage), cost: round2(cost) };
  }
  return out;
}

/** A job's equipment cost: its km × the vehicle's rate (the first vehicle), its hours × each assigned tool's rate. */
export function jobEquipmentCost({ project, km = 0, hours = 0, rates = {} }) {
  const lines = [];
  const vehicle = Object.entries(rates).find(([, r]) => r.unit === 'km' && r.perUnit != null);
  if (vehicle && km > 0) lines.push({ id: vehicle[0], name: vehicle[1].name, basis: `${round2(km)} km × ${moneyShort(vehicle[1].perUnit)}`, amount: round2(km * vehicle[1].perUnit) });
  for (const id of Array.isArray(project?.equipment) ? project.equipment : []) {
    const r = rates[id];
    if (r && r.unit === 'hour' && r.perUnit != null && hours > 0) lines.push({ id, name: r.name, basis: `${round2(hours)} h × ${moneyShort(r.perUnit)}`, amount: round2(hours * r.perUnit) });
  }
  return { total: round2(lines.reduce((t, l) => t + l.amount, 0)), lines };
}

// ---- forecast + hiring (phase 3) ----------------------------------------------------------------------------------

/**
 * Forecast profit at completion for a job, both versions: what's earned at completion (the contract, or hours × rate)
 * − job costs so far − labour so far − the remaining estimate costed by who it's assigned to − overhead on all hours.
 *   ratesOf(uid) → personRates for that person on this job; pieceOf(task) → piece price or null.
 * Used by the add-person wizard's before / after preview.
 */
export function forecastJob({ cfo, contract = 0, tasks = [], assigneeOf = (t) => t.assignee || null, ratesOf, pieceOf = (t) => (t.piece?.price ? num(t.piece.price) : null), me = null }) {
  if (!cfo) return null;
  let remLabourA = 0; let remLabourP = 0; let remHours = 0;
  const parents = new Set(tasks.filter((t) => t.parentId && num(t.estimatedMinutes) > 0).map((t) => t.parentId));
  for (const t of tasks) {
    if (t.status === 'done' || t.bucket || parents.has(t.id)) continue;
    const h = num(t.estimatedMinutes) / 60;
    const piece = pieceOf(t);
    remHours += h;
    if (piece != null) { remLabourA += piece; remLabourP += piece; continue; }
    const r = ratesOf(assigneeOf(t) || me);
    remLabourA += h * num(r?.actual); remLabourP += h * num(r?.paidOut);
  }
  const totalHours = num(cfo.hours) + remHours;
  const oh = cfo.overheadRate && BILLABLE_JOB_TYPES.has(cfo.type) ? { a: totalHours * num(cfo.overheadRate.actual), p: totalHours * num(cfo.overheadRate.paidOut) } : { a: 0, p: 0 };
  const revenue = cfo.type === 'fixed' ? (num(contract) || num(cfo.revenue?.contract)) : num(cfo.revenue?.amount) + remHours * (num(cfo.perHour?.revenue) || 0);
  const actual = round2(revenue - num(cfo.jobCosts) - num(cfo.labour?.actual) - remLabourA - oh.a);
  const paidOut = round2(revenue - num(cfo.jobCosts) - num(cfo.labour?.paidOut) - remLabourP - oh.p);
  return {
    revenue: round2(revenue), remainingHours: round2(remHours), totalHours: round2(totalHours),
    profit: { actual, paidOut },
    perHour: { actual: totalHours > 0 ? round2(actual / totalHours) : null, paidOut: totalHours > 0 ? round2(paidOut / totalHours) : null },
    marginPct: revenue > 0 ? { actual: Math.round((actual / revenue) * 100), paidOut: Math.round((paidOut / revenue) * 100) } : null,
  };
}

/**
 * "Can we afford to hire for this?" (CFO §13), from rows of work of one kind over a window:
 *   rows: [{ revenue, jobCosts, overhead, hours, lj: hours of the owner's time in it }]
 *   affordable/h = (revenue − job costs − overhead) ÷ hours — the most this work can pay and break even
 *   needed/h     = market wage × (1 + burden) ÷ (1 − target margin)
 * → { affordable, needed, verdict: 'yes' | 'not_yet' | 'set_target' | 'no_data', gap, hoursPerWeek, weeks, cashWeeks }
 */
export function hireCheck({ rows = [], marketWage = 0, burdenPct = 0, targetMarginPct = null, days = 90, hoursPerWeekHire = 40, bank = null }) {
  const sum = (k) => rows.reduce((t, r) => t + num(r[k]), 0);
  const hours = sum('hours');
  if (!(hours > 0)) return { verdict: 'no_data', affordable: null, needed: null, gap: null, hours: 0 };
  const affordable = round2((sum('revenue') - sum('jobCosts') - sum('overhead')) / hours);
  const loaded = num(marketWage) * (1 + num(burdenPct) / 100);
  const weeks = days / 7;
  const hoursPerWeek = round2(sum('lj') / weeks);
  const weeklyCost = loaded * num(hoursPerWeekHire);
  const cashWeeks = bank != null && weeklyCost > 0 ? Math.floor(num(bank) / weeklyCost) : null;
  if (targetMarginPct == null) return { verdict: 'set_target', affordable, needed: null, loaded: round2(loaded), gap: null, hours: round2(hours), hoursPerWeek, cashWeeks };
  const needed = round2(loaded / (1 - num(targetMarginPct) / 100));
  const gap = round2(affordable - needed);
  return { verdict: !(marketWage > 0) ? 'no_data' : gap >= 0 ? 'yes' : 'not_yet', affordable, needed, loaded: round2(loaded), gap, hours: round2(hours), hoursPerWeek, cashWeeks };
}
